<template>
  <div class="retention-defaults-by-product">
    <div v-if="isLoading" class="empty">Loading…</div>
    <div v-else-if="rows.length === 0" class="empty">
      No products found in the catalog for this app.
    </div>

    <template v-else>
      <div class="search-row">
        <div class="search-input-wrap">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search by product ID (e.g. .t1, weekly)"
          />
          <i
            v-if="searchQuery"
            class="fas fa-times clear-icon"
            @click="searchQuery = ''"
          ></i>
        </div>
        <span class="search-summary">
          {{ filteredRows.length }} / {{ rows.length }}
        </span>
      </div>

      <div v-if="filteredRows.length === 0" class="empty">
        No products match "{{ searchQuery }}".
      </div>
      <div v-else class="table-wrap">
        <table class="defaults-table">
          <thead>
            <tr>
              <th class="col-product">Product ID</th>
              <th class="col-configured">Configured</th>
              <th class="col-messages">Messages in Use</th>
              <th class="col-updated">Last Updated</th>
              <th class="col-chevron"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredRows"
              :key="row.productId"
              class="data-row"
              @click="goToProduct(row.productId)"
            >
              <td><code class="product-id">{{ row.productId }}</code></td>
              <td>
                <div class="progress-cell">
                  <span class="progress-label">{{ row.configuredCount }} / {{ supportedLocales.length }}</span>
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: `${row.progressPct}%` }"></div>
                  </div>
                </div>
              </td>
              <td>
                <div v-if="row.messageNames.length === 0" class="muted">—</div>
                <div v-else class="msg-chips">
                  <span
                    v-for="(name, i) in row.messageNames.slice(0, 3)"
                    :key="i"
                    class="msg-chip"
                    :title="name"
                  >
                    {{ truncate(name, 24) }}
                  </span>
                  <span v-if="row.messageNames.length > 3" class="msg-overflow">
                    +{{ row.messageNames.length - 3 }}
                  </span>
                </div>
              </td>
              <td>
                <span v-if="row.lastUpdated" class="updated">{{ formatRelative(row.lastUpdated) }}</span>
                <span v-else class="muted">—</span>
              </td>
              <td class="col-chevron">
                <i class="fas fa-chevron-right"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { SUPPORTED_LOCALES } from '../../constants/retentionLocales';

export default {
  data() {
    return {
      supportedLocales: SUPPORTED_LOCALES,
      searchQuery: ''
    };
  },
  computed: {
    defaults() {
      return this.$store.getters['retention/defaults'] || [];
    },
    products() {
      return this.$store.getters['retention/productIds'] || [];
    },
    retentionMessages() {
      return this.$store.getters.retentionMessages || [];
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingDefaults'];
    },
    rows() {
      const byProduct = new Map();
      for (const p of this.products) {
        byProduct.set(p.productId, []);
      }
      for (const d of this.defaults) {
        if (!byProduct.has(d.productId)) byProduct.set(d.productId, []);
        byProduct.get(d.productId).push(d);
      }

      const messageNameById = new Map();
      for (const m of this.retentionMessages) {
        messageNameById.set(m.id, m.name || m.header || '(unnamed)');
      }

      const totalLocales = this.supportedLocales.length;

      const rows = [];
      for (const [productId, productDefaults] of byProduct) {
        const names = new Set();
        let lastUpdated = 0;
        for (const d of productDefaults) {
          const name = messageNameById.get(d.messageId) || '(unknown)';
          names.add(name);
          if (d.configuredAt && d.configuredAt > lastUpdated) lastUpdated = d.configuredAt;
        }
        const configuredCount = productDefaults.length;
        rows.push({
          productId,
          configuredCount,
          progressPct: totalLocales > 0 ? Math.round((configuredCount / totalLocales) * 100) : 0,
          messageNames: Array.from(names),
          lastUpdated: lastUpdated || null
        });
      }
      rows.sort((a, b) => {
        if (b.configuredCount !== a.configuredCount) return b.configuredCount - a.configuredCount;
        return a.productId.localeCompare(b.productId);
      });
      return rows;
    },
    filteredRows() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r => r.productId.toLowerCase().includes(q));
    }
  },
  methods: {
    goToProduct(productId) {
      this.$router.push({
        name: 'RetentionDefaultProduct',
        params: { aid: this.$store.getters.activeApp, productId }
      });
    },
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? `${text.slice(0, n)}…` : text;
    },
    formatRelative(ts) {
      if (!ts) return '—';
      const now = Date.now();
      const diff = now - ts;
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      const d = new Date(ts);
      return d.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.retention-defaults-by-product {
  flex: 1;
  min-width: 0;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.search-input-wrap {
  position: relative;
  flex: 1;
  max-width: 420px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.82rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 2.1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.88rem;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.search-input:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}

.clear-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.82rem;
  cursor: pointer;
}

.clear-icon:hover {
  color: #fff;
}

.search-summary {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.5);
  font-variant-numeric: tabular-nums;
}

.table-wrap {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.defaults-table {
  width: 100%;
  border-collapse: collapse;
  color: rgba(255, 255, 255, 0.85);
}

.defaults-table th,
.defaults-table td {
  text-align: left;
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.88rem;
  vertical-align: middle;
}

.defaults-table thead th {
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.defaults-table tr.data-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.defaults-table tr.data-row:hover {
  background: rgba(3, 179, 218, 0.06);
}

.defaults-table tr:last-child td {
  border-bottom: none;
}

.col-product   { width: 35%; }
.col-configured { width: 200px; }
.col-messages  { width: auto; }
.col-updated   { width: 130px; }
.col-chevron   { width: 30px; text-align: right; color: rgba(255, 255, 255, 0.35); }

.product-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.83rem;
  color: rgba(255, 255, 255, 0.92);
  background: none;
}

.progress-cell {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.progress-label {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
  font-variant-numeric: tabular-nums;
}

.progress-bar {
  width: 100%;
  height: 5px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #03B3DA;
  border-radius: 999px;
  transition: width 0.2s ease;
}

.msg-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.msg-chip {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(3, 179, 218, 0.1);
  border: 1px solid rgba(3, 179, 218, 0.25);
  border-radius: 999px;
  font-size: 0.74rem;
  color: #b8e8f2;
}

.msg-overflow {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.5);
  padding: 2px 4px;
}

.muted { color: rgba(255, 255, 255, 0.35); }

.updated {
  font-size: 0.84rem;
  color: rgba(255, 255, 255, 0.6);
}
</style>
